import { Dropdown, Form, message, Space, Table, Tag } from "antd";
import { AppleOutlined, DownOutlined, MobileOutlined, WechatOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

import { getAllUserList } from "../service/api/userAPI";
import UserEditFormModal from "../modal/UserEditFormModal";
import UserViewStudyProgressModal from "../modal/UserViewStudyProgressModal";
import SendNotificationModal from "../modal/SendNotificationModal";
import ViewLogsModal from "../modal/ViewLogsModal";
import CountryLocation from "../badge/countryLocation";
import FilterForm from "../component/FilterForm";

const UserTable = () => {
    const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
    const [dataSource, setDataSource] = useState([]);
    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: 10,
        },
        filters: {},
    });
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);

    const updateTable = async (pagination = tableParams.pagination, filters = tableParams.filters) => {
        setLoading(true);
        try {
            const res = await getAllUserList(pagination.current - 1, pagination.pageSize, filters);
            setDataSource(res.data.content.map(item => ({
                id: item.id,
                name: item.name,
                country: item.country,
                region: item.region,
                city: item.city,
                email: item.email,
                userType: item.userType,
                timestamp: new Date(item.createDate).toLocaleString(),
                dailyUsedToken: item.dailyUsedToken,
                totalDailyToken: item.totalDailyToken,
                deviceList: item.deviceList,
                subscriptionId: item.subscriptionId
            })));

            setTableParams(prev => ({
                ...prev,
                pagination: {
                    ...pagination,
                    total: res.data.totalElements,
                    showTotal: total => `Total ${total} users`
                }
            }));
        } catch (error) {
            message.error("Failed to load user data.");
        } finally {
            setLoading(false);
        }
    };

    const onFilterFinish = (values) => {
        setTableParams(prev => ({
            ...prev,
            filters: values,
            pagination: {
                ...prev.pagination,
                current: 1,
            },
        }));
    };

    const filterConfig = [
        { type: 'input', name: 'userId', placeholder: 'ID' },
        { type: 'input', name: 'name', placeholder: 'Name' },
        {
            type: 'select', name: 'authType', placeholder: 'Type',
            options: [
                { value: 'ios', label: 'IOS' },
                { value: 'wechat', label: 'Wechat' }
            ]
        },
        {
            type: 'select', name: 'subType', placeholder: 'Subscription Type',
            options: [
                { value: 'vocabAI_900_1m', label: 'Monthly' },
                { value: 'vocabAI_3m', label: 'Quarterly' },
                { value: 'vocabAI_6000_1y', label: 'Annually' }
            ]
        }
    ];

    const renderActionMenu = (row) => (
        <Dropdown
            menu={{
                items: [
                    { key: 'updateToken', label: (<UserEditFormModal buttonName="Update Token" user={row} callback={updateTable} />) },
                    { key: 'viewStudyProgress', label: (<UserViewStudyProgressModal buttonName="View Study Progress" user={row} callback={updateTable} />) },
                    {
                        key: 'sendNotification',
                        label: (<SendNotificationModal buttonName="Send notifications" user={row} />),
                        disabled: !(row.deviceList && row.deviceList.length > 0)
                    },
                    { key: 'viewLog', label: (<ViewLogsModal buttonName="View Logs" user={row} />) }
                ]
            }}
        >
            <a onClick={e => e.preventDefault()}>
                <Space>Action <DownOutlined /></Space>
            </a>
        </Dropdown>
    );

    const fullColumns = [
        { key: 'id', title: 'ID', dataIndex: 'id', width: 50 },
        {
            key: 'userType', title: 'Type', dataIndex: 'userType', width: 50,
            render: (text) => (text === 'wechat' ? <WechatOutlined /> : <AppleOutlined />)
        },
        { key: 'name', title: 'Name', dataIndex: 'name' },
        {
            key: 'ip', title: 'IP',
            render: row => row.country ? <CountryLocation countryCode={row.country} region={row.region} city={row.city} /> : ''
        },
        {
            key: 'plan', title: 'Plan', width: 200,
            render: row => row.subscriptionId ? <Tag color="gold">{row.subscriptionId}</Tag> : 'Normal'
        },
        { key: 'email', title: 'Email', dataIndex: 'email' },
        {
            key: 'totalDailyToken', title: 'Daily Token Usage',
            render: row => <span>{row.dailyUsedToken + "/" + (row.subscriptionId ? '∞' : row.totalDailyToken)}</span>
        },
        {
            key: 'deviceList', title: 'Devices', width: 250,
            render: row => row.deviceList?.map((e, idx) => (
                <Tag key={idx} icon={<MobileOutlined />} color="#3b5999">{e}</Tag>
            ))
        },
        { key: 'timestamp', title: 'Created At', dataIndex: 'timestamp', width: 200 },
        {
            title: 'Action', key: 'operation', fixed: 'right', width: 100,
            render: row => renderActionMenu(row)
        }
    ];

    const mobileColumns = [
        { title: 'ID', dataIndex: 'id', width: 50 },
        { title: 'Name', dataIndex: 'name' },
        {
            title: 'Action', key: 'operation', fixed: 'right', width: 100,
            render: row => renderActionMenu(row)
        }
    ];

    const columns = isMobile ? mobileColumns : fullColumns;

    const handleTableChange = (pagination, filters, sorter) => {
        setTableParams(prev => ({
            ...prev,
            pagination,
            // keep filters and apply new sorters if needed
        }));
    };

    useEffect(() => {
        updateTable(tableParams.pagination, tableParams.filters);
    }, [
        tableParams.pagination.current,
        tableParams.pagination.pageSize,
        JSON.stringify(tableParams.filters)
    ]);

    return (
        <>
            <FilterForm form={form} onFilterFinish={onFilterFinish} tableParams={tableParams} filterConfig={filterConfig} />
            <Table
                rowKey="id"
                columns={columns}
                dataSource={dataSource}
                pagination={tableParams.pagination}
                loading={loading}
                onChange={handleTableChange}
            />
        </>
    );
};

export default UserTable;
